<script setup>
import { useScrollToComponent } from '~/composables/useScrollToComponent'

const props = defineProps({
  title: { type: String, required: true },
  text: { type: String, default: '' },
  buttonText: { type: String, required: true },
  targetId: { type: String, default: 'enquiry' },
})

const { scrollToComponent } = useScrollToComponent()
</script>

<template>
  <section
    v-reveal
    class="reveal cta-banner relative overflow-hidden w-11/12 mx-auto my-12 rounded-2xl px-6 py-8 text-left lg:w-4/5 lg:px-12"
  >
    <div class="relative grid gap-6 items-center lg:grid-cols-[auto_1fr_auto]">
      <!-- Fehér ikon narancs karikában. A karikát SVG rajzolja, hogy a
           szekció beúszásakor körbe tudjon futni (lásd .cta-ring-line). -->
      <span class="cta-icon relative shrink-0 hidden place-items-center lg:grid" aria-hidden="true">
        <svg class="cta-ring absolute inset-0 w-full h-full" viewBox="0 0 80 80" fill="none">
          <circle class="cta-ring-track" cx="40" cy="40" r="37" />
          <circle class="cta-ring-line" cx="40" cy="40" r="37" />
        </svg>
        <UIcon name="i-heroicons-chat-bubble-left-right" class="cta-glyph relative w-10 h-10 whiteText" />
      </span>
      <div class="whiteText">
        <h2 class="InterExtraBold text-xl leading-snug lg:text-2xl">{{ title }}</h2>
        <p v-if="text" class="InterMedium mt-3 text-base opacity-90">{{ text }}</p>
      </div>
      <button
        type="button"
        class="cta-button bg-orange whiteText InterExtraBold px-7 py-4 rounded-md w-full inline-flex items-center justify-center gap-2 lg:w-max"
        @click="scrollToComponent(targetId)"
      >
        {{ buttonText }}
        <span class="cta-arrow" aria-hidden="true">→</span>
      </button>
    </div>
  </section>
</template>

<style scoped>
/* Csak kékek keverednek: a korábbi narancs fényfolt a sötétkéken sáros
   kék-sárga átmenetet adott. A narancs így már csak éles élként jelenik meg
   (karika, gomb), nem elmosódó ködként. */
.cta-banner {
  background: linear-gradient(135deg, #16233D 0%, #1B2A4A 55%, #24365E 100%);
}

.cta-icon {
  width: 76px;
  height: 76px;
}

.cta-ring circle {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
}
.cta-ring-track {
  stroke: rgba(255, 255, 255, .14);
}
.cta-ring-line {
  stroke: rgb(255, 118, 24);
  /* 2 * PI * 37 ~ 232.5 - a teljes kerület, ennyivel van kitolva induláskor. */
  stroke-dasharray: 233;
  stroke-dashoffset: 233;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

/* A .reveal-in a szekció gyökerére kerül, amikor a görgetés ideér
   (plugins/reveal.js) — innen indul a karika körberajzolása. */
.cta-banner.reveal-in .cta-ring-line {
  animation: ctaRingDraw 1.2s cubic-bezier(.22, .61, .36, 1) .15s forwards;
}
.cta-glyph {
  opacity: 0;
  transform: scale(.7);
}
.cta-banner.reveal-in .cta-glyph {
  animation: ctaGlyphIn .5s cubic-bezier(.22, .61, .36, 1) .55s forwards;
}

.cta-button {
  transition: background-color .25s ease, box-shadow .25s ease, transform .25s cubic-bezier(.22, .61, .36, 1);
}
.cta-button:hover {
  background: #ef6100;
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(255, 118, 24, .35);
}
.cta-arrow {
  transition: transform .25s cubic-bezier(.22, .61, .36, 1);
}
.cta-button:hover .cta-arrow {
  transform: translateX(5px);
}

@keyframes ctaRingDraw {
  to { stroke-dashoffset: 0; }
}

@keyframes ctaGlyphIn {
  to { opacity: 1; transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .cta-ring-line {
    stroke-dashoffset: 0;
  }
  .cta-glyph {
    opacity: 1;
    transform: none;
  }
  .cta-banner.reveal-in .cta-ring-line,
  .cta-banner.reveal-in .cta-glyph {
    animation: none;
  }
  .cta-button:hover { transform: none; }
  .cta-arrow { transition: none; }
}
</style>
